import moment from "moment";
import Address from "./address";
import { ContractType } from "./contract";
import { DeliveryMethod } from "./deliveryMethod";
import { ErpSystem } from "./erpSystem";
import { getTodaysDate } from "common/formatters";

export interface MerchantThin {
    id: string;
    name: string;
    appStoreUrl: string;
    playStoreUrl: string;
    giveUpFee: number;
}

interface ContractDeliveryMethodMapping {
    contractType: ContractType;
    deliveryMethod: DeliveryMethod;
}

export default interface Merchant extends MerchantThin {
    enableAutoOffer: boolean;
    enablePassOnFill: boolean;
    enableAutoPosition: boolean;
    enableRiskServiceBypass: boolean;
    enableContractImport: boolean;
    enablePriceFromBalance: boolean;
    isDetermineLaterAllowed: boolean;
    hasErpIntegration: boolean;
    marketAutoOfferEnabled: boolean;
    endOfDay: string;
    endOfDayUtc: string;
    mailingAddress: Address;
    phone: string;
    email: string;
    websiteUrl: string;
    contractDeliveryMethodMappings: ContractDeliveryMethodMapping[];
    giveUpFee: number;
    erpSystem?: ErpSystem;
    generalSettings: GeneralSettings;
    useCustomFields: boolean;
    featureFlags: MerchantFeatureFlag[];
    contractTypes: ContractType[];
    cropYearStartMonth: number;
    restrictLocationsOnContractsToMappings: boolean;
}


export interface GeneralSettings {
    displayPaymentOption: boolean;
    displayRegion: boolean;
    displayContractLocation: boolean;
    displayInventoryLocation: boolean;
    displayDeliveryTermsForBuy: boolean;
    displayDeliveryTermsForSell: boolean;

    displayCustomerContractIdForSell: boolean;
    displayCustomerContractIdForBuy: boolean;
    makeCustomerContractIdRequired: boolean;
    useCustomerContractIdAsErpId: boolean;

    defaultDeferPayInterestRate?: number;
    isCommentDeletionAllowed: boolean;
    isPriceAtOpenEnabled: boolean;
    sendNotificationToCustomerDefault: boolean;

    displayErpContractId: boolean;
}

interface MerchantFeatureFlag {
    code: MerchantFeature;
    name: string;
    description: string;
    value: boolean;
}

export function isFeatureEnabled(merchant: Merchant, feature: MerchantFeature): boolean {
    return merchant.featureFlags.some(m => m.code === feature && m.value);
}

export function isDeferPayInterestEnabled(merchant: Merchant): boolean {
    return (merchant.generalSettings.defaultDeferPayInterestRate ?? 0) > 0;
}

export enum MerchantFeature {
    HedgeRecords = "HedgeRecords",
    ShowCustomerInvitation = "ShowCustomerInvitation",
    ThirdPartyAppIntegration = "ThirdPartyAppIntegration",
    SendToErpToggle = "SendToErpToggle",
    AutomatedHedgeRecords = "AutomatedHedgeRecords",
    HedgeRecordsAsLandingPage = "HedgeRecordsAsLandingPage",
    ManuallyAddPartnerMappings = "ManuallyAddPartnerMappings",
    ViewPurchaseSalesByPositionGroup = "PurchaseSalesByPositionGroup",
    ESignature = "ESignature",
    BasisTypeFilter = "BasisTypeFilter",
    BasisOffers = "BasisOffers",
    ContractAmendments = "ContractAmendments",
    SplitContract = "SplitContract",
    BushelOnlyContract = "BushelOnlyContract",
    PartialRollContract = "PartialRollContract",
    LocationTypes = "LocationTypes",
    ShowLiveLedgerView = "ShowLiveLedgerView",
    UseHtaFeeSchedule = "UseHtaFeeSchedule",
    PurchaseSaleTerminology = "PurchaseSaleTerminology"
}

/** merchant features that aren't listed in the main feature flags interface (because eg they have dedicated UI elsewhere) */
export const UNLISTED_MERCHANT_FEATURES = new Set<MerchantFeature>([
    MerchantFeature.BasisOffers, // toggle in the merchant "Contract Type Settings" when "Basis" is selected 
    MerchantFeature.PurchaseSaleTerminology, // displayed in "Merchant Settings"
])

export function getStartEndBasedOnMerchant(merchantEndOfDayUtc: string, date?: Date): [Date, Date] {
    if (merchantEndOfDayUtc == null)
        return [moment(date).add(-1, "days").toDate(), date ?? getTodaysDate()];

    const eodInUtc = moment.utc(merchantEndOfDayUtc, "hh:mm:ss");
    const utcNow = moment.utc();

    if (utcNow.isBefore(eodInUtc)) {
        eodInUtc.add(-1, "days");
    }
    if (date) {
        eodInUtc.year(date.getFullYear());
        eodInUtc.month(date.getMonth());
        eodInUtc.date(date.getDate() - 1);
    }
    const startDay = eodInUtc.toDate();
    const endDay = eodInUtc.add(1, "days").toDate();
    return [startDay, endDay];
}
